<template>
    <div class="container mt-4">
        <h2>Users</h2>
        <!-- Przycisk do otwierania modala -->
        <button class="btn btn-success mb-3" @click="showAddUserModal = true">
            <i class="bi bi-plus-lg"></i> Add User
        </button>
        <!-- Tabela użytkowników -->
        <div class="table-responsive">
            <table class="table table-striped table-hover">
                <thead class="table-dark">
                    <tr>
                        <th scope="col">#</th>
                        <th scope="col">Full Name</th>
                        <th scope="col">Email</th>
                        <th scope="col">Role</th>
                        <th scope="col">Distributors</th>
                        <th scope="col">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(user, index) in users" :key="user.id">
                        <th scope="row">{{ index + 1 }}</th>
                        <td>{{ user.fullName }}</td>
                        <td>{{ user.email }}</td>
                        <td>{{ getUserRole(user.role) }}</td>
                        <td>
                            <span v-if="loadingDistributors[user.id as string]">
                                <div class="spinner-border spinner-border-sm text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                            </span>
                            <div class="d-flex flex-wrap gap-2" v-else>
                                <bs5-badge-with-close
                                    v-for="(distributor, dIndex) in userDistributors[user.id as string]" :key="dIndex" @remove="removeDistributor(user, distributor)">
                                    {{ distributor.name }}
                                </bs5-badge-with-close>
                            </div>
                        </td>
                        <td>
                            <bs5-group-button>
                                <bs5-edit-button @click="openEditModal(user)" />
                                <bs5-base-button @click="openSetPasswordModal(user)">Change password</bs5-base-button>
                                <bs5-base-button variant="warning" @click="openAssignDistributorModal(user)">
                                    Assign Distributor
                                </bs5-base-button>
                                <bs5-delete-button @click="removeUser(user)" :loading="removeLoading" />
                            </bs5-group-button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <AddUserModal :visible="showAddUserModal" @close="showAddUserModal = false" @user-added="fetchUsers" />
        <EditUserModal :visible="showEditUserModal" :userId="selectedUserId" @close="showEditUserModal = false"
            @user-updated="fetchUsers" />
        <SetPasswordModal :visible="showSetPasswordModal" :userId="selectedUserId" @close="showSetPasswordModal = false"
            @password-set="fetchUsers" />
        <AssignDistributorModal :visible="showAssignDistributorModal" :userId="selectedUserId"
            @close="showAssignDistributorModal = false" @distributor-assigned="fetchUsers" />
        <!-- Załaduj więcej danych -->
        <button v-if="!loading" class="btn btn-secondary mt-3" @click="fetchUsers">Refresh</button>
        <div v-if="loading" class="text-center mt-3">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import userService from "@/services/user.service";
import { User } from "@/models/user.model";
import { UserRole } from "@/enums/user-role.enum";
import { Distributor } from "@/models/distributor.model";
import AddUserModal from "./modals/AddUserModal.vue"
import EditUserModal from "./modals/EditUserModal.vue";
import SetPasswordModal from "./modals/SetPasswordModal.vue";
import AssignDistributorModal from "./modals/AssignDistributorModal.vue";
import { showToast } from "@/utils/toast";


export default defineComponent({
    components: {
        AddUserModal,
        EditUserModal,
        SetPasswordModal,
        AssignDistributorModal
    },
    data() {
        return {
            users: ref<User[]>([]), // Używamy typowania z User.ts
            loading: ref(false),
            removeLoading: ref(false),
            showAddUserModal: ref(false), // Nowy stan modala
            showEditUserModal: ref(false),
            showSetPasswordModal: ref(false),
            showAssignDistributorModal: ref(false),
            selectedUserId: ref<string>(""),
            userDistributors: ref<{ [key: string]: Distributor[] }>({}),
            loadingDistributors: ref<{ [key: string]: boolean }>({}),
        };
    },
    methods: {
        async fetchUsers() {
            try {
                this.loading = true;
                this.users = await userService.getUsers();
                await this.fetchDistributorsForUsers();
            } catch (error) {
                console.error("Error fetching users:", error);
            } finally {
                this.loading = false;
            }
        },
        async fetchDistributorsForUsers() {
            for (const user of this.users) {
                await this.fetchDistributorsForUser(user);
            }
        },

        async fetchDistributorsForUser(user: User){
            try {
                    this.loadingDistributors[user.id as string] = true;
                    this.userDistributors[user.id as string] = await userService.getUserDistributor(user.id as string);
                } catch (error) {
                    console.error(`Error fetching distributors for user ${user.id}:`, error);
                    this.userDistributors[user.id as string] = [];
                } finally {
                    this.loadingDistributors[user.id as string] = false;
                }
        },
        async removeUser(user: User) {
            try {
                this.removeLoading = true;
                await userService.deleteUser(user.id as string);
                await this.fetchUsers();
            } catch (error) {
                console.error("Error fetching users:", error);
            } finally {
                this.removeLoading = false;
            }
        },
        async removeDistributor(user: User, distributor: Distributor){
            try{
                await userService.removeDistributor(user.id as string, distributor.id as string);
                await this.fetchDistributorsForUser(user);
                showToast("Distributor removed!", "success");
            }  
            catch(e){
                showToast("Failed to remove distributor!", "error");
                console.log(e);
            }
        },
        getUserRole(role: UserRole): string {
            return UserRole[role] || "Unknown";
        },
        openEditModal(user: User) {
            this.selectedUserId = user.id as string;
            this.showEditUserModal = true;
        },
        openSetPasswordModal(user: User) {
            this.selectedUserId = user.id as string;
            this.showSetPasswordModal = true;
        },
        openAssignDistributorModal(user: User) {
            this.selectedUserId = user.id as string;
            this.showAssignDistributorModal = true;
        },
    },
    mounted() {
        this.fetchUsers();
    },
});
</script>

<style scoped>
.table th,
.table td {
    vertical-align: middle;
}
</style>